import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  HStack,
  VStack,
  Center,
  Icon,
  Heading,
  Divider,
  Box,
  Checkbox,
} from '@chakra-ui/react';

import {
  useGetAllRolesQuery,
  useGetTeamMembersByRoleIdQuery,
  useReassignTeamMemberRoleMutation,
  useDeleteRoleMutation,
  displayNotification,
} from '../../../services';
import { useTypedSelector } from '../../../hooks';
import { CenteredSpinner } from '../../../components';

import { ReactComponent as RedTrashIcon } from '../../../assets/icons/red-trash.svg';
import { useForm, useFieldArray } from 'react-hook-form';
import { RolesMenu } from '.';
import { ChangeEvent, useState } from 'react';
import {
  isMutationSuccess,
  notifyMutationFailure,
} from '../../../utils/mutation-feedback';

export const DeleteRoleModal = ({
  isOpen,
  onClose,
  role,
}: {
  isOpen: boolean;
  onClose: () => void;
  role: { id: number; name: string };
}) => {
  const [isDisabledCheck, setIsDisabledCheck] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState(0);
  const { userDetails } = useTypedSelector(state => state.auth);

  const { control, getValues } = useForm<{
    rolesPerUsers: { teamMemberId: number; roleId: number }[];
  }>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rolesPerUsers',
  });

  const { data, isLoading } = useGetTeamMembersByRoleIdQuery(role.id);

  const { data: allRoles } = useGetAllRolesQuery(
    {
      basicInfoId: Number(userDetails?.companyBasicInfo.id),
      page: 0,
      size: 10000,
    },
    {
      skip: !userDetails,
    }
  );

  const [reassignTeamMemberRole, { isLoading: isReassignLoading }] =
    useReassignTeamMemberRoleMutation();
  const [deleteRole, { isLoading: isDeleteLoading }] = useDeleteRoleMutation();

  const handleDeleteRole = async () => {
    if (fields.length) {
      const reassignResponse = await reassignTeamMemberRole(
        getValues('rolesPerUsers')
      );

      // Stop here on a failed reassignment: deleting the role anyway would
      // strand the members this modal just promised to move.
      if (!isMutationSuccess(reassignResponse)) {
        notifyMutationFailure(reassignResponse, {
          title: 'Could not reassign the members of this role',
          description:
            'The role was not deleted and no member was moved. Please try again.',
        });
        return;
      }
    }

    const deleteResponse = await deleteRole({ id: role.id });

    if (!isMutationSuccess(deleteResponse)) {
      notifyMutationFailure(deleteResponse, {
        title: `Could not delete the ${role.name} role`,
        description: 'The role was not deleted. Please try again.',
      });
      return;
    }

    displayNotification({
      title: `Successfully deleted ${role.name} role`,
      description: `Your have deleted ${role.name} role`,
    });

    onClose();
  };

  const handleChangeRolePerUser = (roleId: number, teamMemberId: number) => {
    const rolePerUserIndex = getValues('rolesPerUsers').findIndex(
      roleByUser => roleByUser.teamMemberId === teamMemberId
    );

    if (rolePerUserIndex) remove(rolePerUserIndex);
    append({ roleId, teamMemberId });
  };

  const handelCheckAllUsers = (e: ChangeEvent<HTMLInputElement>) => {
    remove();
    if (e.target.checked && data?.data)
      data.data.forEach(({ id }) =>
        append({ roleId: selectedRoleId, teamMemberId: id })
      );
  };

  return (
    <>
      <Modal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        <ModalContent display="flex" minH="32">
          {isLoading ? (
            <CenteredSpinner />
          ) : (
            <Box>
              <ModalHeader>
                <Center
                  bg="custom_red.2"
                  borderRadius="50"
                  w="10"
                  h="10"
                  outline="solid 8px"
                  outlineColor="custom_red.3"
                >
                  <Icon as={RedTrashIcon} />
                </Center>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack align="start" color="custom_blue.1">
                  <Heading fontSize="md" fontWeight="semibold">
                    Are you sure you want to delete this role?
                  </Heading>
                  <Text fontSize="sm">
                    You are about to delete the {role.name} role.
                    {data?.data.length
                      ? 'The following users are currently assigned to this role and need to be reassigned:'
                      : null}
                  </Text>

                  {data?.data.length && allRoles?.data.content && (
                    <Box>
                      {data.data.map(({ id: teamMemberId, firstName }) => (
                        <HStack
                          key={teamMemberId}
                          justify="space-between"
                          w="full"
                        >
                          <Text fontSize="xs" color="custom_blue.1">
                            {firstName}
                          </Text>

                          {allRoles.data.content && (
                            <RolesMenu
                              roles={allRoles.data.content.filter(
                                ({ id }) => id !== role.id
                              )}
                              onChangeMenuItem={roleId =>
                                handleChangeRolePerUser(roleId, teamMemberId)
                              }
                            />
                          )}
                        </HStack>
                      ))}

                      <HStack>
                        <HStack>
                          <Checkbox
                            isDisabled={isDisabledCheck}
                            colorScheme="orange"
                            onChange={handelCheckAllUsers}
                          />

                          <Text fontSize="sm" color="custom_blue.1">
                            Select All
                          </Text>
                        </HStack>

                        <RolesMenu
                          roles={allRoles.data.content.filter(
                            ({ id }) => id !== role.id
                          )}
                          onChangeMenuItem={roleId => {
                            setIsDisabledCheck(false);
                            setSelectedRoleId(roleId);
                          }}
                        />
                      </HStack>
                      <Divider />
                    </Box>
                  )}
                </VStack>
              </ModalBody>

              <ModalFooter display="flex" flexDir="column" gap="2">
                <Button
                  bg="error.600"
                  _hover={{ bg: 'error.600' }}
                  color="white"
                  fontWeight="semibold"
                  fontSize="sm"
                  w="full"
                  onClick={handleDeleteRole}
                  isDisabled={!fields.length && !!data?.data.length}
                  isLoading={isReassignLoading || isDeleteLoading}
                >
                  Yes, Delete
                </Button>
                <Button
                  onClick={onClose}
                  w="full"
                  color="custom_blue.1"
                  fontWeight="semibold"
                  fontSize="sm"
                  boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                  border="1px"
                  borderColor="custom_blue.3"
                  bg="none"
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Box>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
